import React, { useRef, useState, useEffect } from 'react';
import { HistoryDataProps, Timeframes } from '@/types/coins/history';
import SingleCoinLine from '@/components/(postTypes)/symbols/single/coin/SingleCoinLine';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { CHART_DEFAULT_ORDER, isChartRange, pointsInRange } from '@/utils/coins/chartRanges';
import { toPersianNumber } from '@/utils/persianUtils';

interface SingleCoinHeader extends HistoryDataProps {
    timePeriod: string;
    updateTimePeriod: (newTimePeriod: string) => void;
    typeId: number;
    displayName: string;
    symbol?: string;
    changes?: Record<string, number>;
    /** "USD" when the gold/currency series is in dollars (the world gold ounce) */
    quoteCurrency?: string;
    /** The selected range is still loading (`history` is the previous one's) */
    loading?: boolean;
    /** Loading the selected range failed (network / server), which doesn't mean it's empty */
    failed?: boolean;
    /** Market-closed hours from the history API */
    closedRanges?: { from: number; to: number }[];
    /** Which history API the page uses. On crypto pages typeId 2 means a stablecoin (USDT), not gold,
     *  so they pass 'crypto'; without it the type is guessed from typeId. */
    historyType?: 'crypto' | 'gold' | 'currency';
}

const timePeriodTranslations: Record<string, string> = {
    '1h': '۱ ساعت',
    '3h': '۳ ساعت',
    '12h': '۱۲ ساعت',
    '24h': '۲۴ ساعت',
    '1d': '۱ روز',
    '7d': '۷ روز',
    '30d': '۱ ماه',
    '3m': '۳ ماه',
    '1y': '۱ سال',
    '3y': '۳ سال',
    '5y': '۵ سال',
};

const timeframeOrder = ['3h', '12h', '24h', '1d', '7d', '30d', '3m', '1y', '3y', '5y'];

const SingleCoinMainChart: React.FC<SingleCoinHeader> = ({
    updateTimePeriod,
    timePeriod,
    history,
    timeframes,
    typeId,
    displayName,
    symbol,
    changes,
    quoteCurrency,
    loading = false,
    failed = false,
    closedRanges,
    historyType: historyTypeProp,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const historyType =
        historyTypeProp ?? (typeId === 2 ? 'gold' : typeId === 3 ? 'currency' : 'crypto');

    // Ranges that really have points (the API's own `timeframes` flags are sometimes wrong)
    const [withData, setWithData] = useState<{ ranges: string[]; unknown: string[] } | null>(null);
    // Ranges that came back empty when opened
    const [emptyRanges, setEmptyRanges] = useState<string[]>([]);

    useEffect(() => {
        setWithData(null);
        setEmptyRanges([]);
        const slug = symbol?.toLowerCase();
        if (!slug) return;
        const ctrl = new AbortController();
        fetch(`/api/coin/history/${encodeURIComponent(slug)}/ranges?type=${historyType}`, {
            signal: ctrl.signal,
        })
            .then((r) => (r.ok ? r.json() : null))
            .then((json) => {
                if (json && Array.isArray(json.ranges)) {
                    setWithData({ ranges: json.ranges, unknown: json.unknown || [] });
                }
            })
            .catch(() => {});
        return () => ctrl.abort();
    }, [symbol, historyType]);

    // The selected range answered with fewer than 2 points → it has nothing to draw
    useEffect(() => {
        if (loading || failed || !history) return;
        const key = historyType === 'gold' ? 'price_in_toman' : 'price';
        if (pointsInRange(history, timePeriod, (h) => h[key]).length < 2) {
            setEmptyRanges((prev) => (prev.includes(timePeriod) ? prev : [...prev, timePeriod]));
        }
    }, [history, timePeriod, loading, failed, historyType]);

    const activeTimePeriods = timeframeOrder.filter((period) => {
        if (emptyRanges.includes(period)) return false;
        if (
            withData &&
            isChartRange(period) &&
            !withData.ranges.includes(period) &&
            !withData.unknown.includes(period)
        ) {
            return false;
        }
        if (timeframes && typeof timeframes === 'object') {
            return Boolean(timeframes[period as keyof Timeframes]);
        }
        if (changes && typeof changes === 'object') {
            return period in changes;
        }
        return ['24h', '7d', '30d', '1y', '5y'].includes(period);
    });
    const listKey = activeTimePeriods.join(',');

    // Selected range is hidden (empty) → open the nearest one that has data
    useEffect(() => {
        if (!activeTimePeriods.length || activeTimePeriods.includes(timePeriod)) return;
        const next =
            CHART_DEFAULT_ORDER.find((p) => activeTimePeriods.includes(p)) || activeTimePeriods[0];
        updateTimePeriod(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listKey, timePeriod]);

    const checkScroll = () => {
        if (!scrollContainerRef.current) return;
        const el = scrollContainerRef.current;
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll <= 0) {
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }

        // Measure distance from the right visual edge (start of list: 1h, 3h, 12h)
        // In WebKit RTL flex, scrollLeft starts at 0 and goes negative towards -maxScroll
        const rawScroll = el.scrollLeft;
        const absScroll = Math.abs(rawScroll);

        // Invert states to match browser RTL engine behavior:
        // At start (showing 1h, 3h on right): Left button is ACTIVE, Right button is DISABLED
        // At end (showing 3y, 5y on left): Left button is DISABLED, Right button is ACTIVE
        setCanScrollLeft(absScroll > 5);
        setCanScrollRight(absScroll < maxScroll - 5);
    };

    useEffect(() => {
        checkScroll();
        const el = scrollContainerRef.current;
        if (el) {
            el.addEventListener('scroll', checkScroll, { passive: true });
        }
        window.addEventListener('resize', checkScroll);
        return () => {
            if (el) el.removeEventListener('scroll', checkScroll);
            window.removeEventListener('resize', checkScroll);
        };
    }, [listKey]);

    useEffect(() => {
        if (!scrollContainerRef.current) return;
        const activeBtn = scrollContainerRef.current.querySelector<HTMLElement>(
            `[data-period="${timePeriod}"]`,
        );
        if (activeBtn) {
            activeBtn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
    }, [timePeriod]);

    // In RTL the list starts at the right: canScrollLeft = chips hidden on the right, canScrollRight = on the left
    const fadeMask = `linear-gradient(to right, ${canScrollRight ? 'transparent' : '#000'} 0, #000 24px, #000 calc(100% - 24px), ${canScrollLeft ? 'transparent' : '#000'} 100%)`;

    return (
        <div className={'py-2 overflow-hidden max-xl:w-full'}>
            <SingleCoinLine
                data={(history as HistoryDataProps['history']) || null}
                timePeriod={timePeriod}
                type={typeId || 0}
                displayName={displayName || ''}
                symbol={symbol}
                symbolType={historyType}
                // Stablecoins (and gold / currency) chart in toman only
                hideCurrencySelector={typeId === 2 || typeId === 3}
                changes={changes}
                quoteCurrency={quoteCurrency}
                closedRanges={closedRanges}
                loading={
                    loading ||
                    (!activeTimePeriods.includes(timePeriod) && activeTimePeriods.length > 0)
                }
            />

            {activeTimePeriods.length > 0 && (
                // RTL row of compact chips; swipe on mobile, edges fade only where more chips are hidden
                <div
                    ref={scrollContainerRef}
                    dir='rtl'
                    className='mt-3 w-full overflow-x-auto no-scrollbar [scrollbar-width:none] [&::-webkit-scrollbar]:hidden scroll-smooth'
                    style={{
                        maskImage: fadeMask,
                        WebkitMaskImage: fadeMask,
                    }}
                >
                    <div className='flex w-max min-w-full gap-1 rounded-xl bg-gray-100/80 dark:bg-white/5 p-1'>
                        {activeTimePeriods.map((period) => {
                            const rawVal =
                                changes?.[period] ??
                                (period === '24h' ? changes?.['24h'] : undefined);
                            const changeVal =
                                typeof rawVal === 'number'
                                    ? rawVal
                                    : typeof rawVal === 'string'
                                      ? parseFloat(rawVal)
                                      : undefined;
                            const hasVal = typeof changeVal === 'number' && !isNaN(changeVal);
                            const isPos = hasVal ? changeVal >= 0 : true;
                            const isActive = timePeriod === period;

                            return (
                                <button
                                    key={period}
                                    type='button'
                                    data-period={period}
                                    aria-pressed={isActive}
                                    className={`flex flex-1 shrink-0 min-w-[56px] flex-col items-center justify-center gap-0.5 rounded-lg px-2 py-1 transition-colors duration-200 select-none cursor-pointer ${
                                        isActive
                                            ? // Dark: a light surface, not solid blue (red/green % on blue-600 was ~2:1 contrast)
                                              'bg-white dark:bg-white/10 text-blue-600 dark:text-white shadow-xs dark:ring-1 dark:ring-white/20'
                                            : 'text-gray-600 dark:text-gray-300 hover:bg-white/70 dark:hover:bg-white/[0.06]'
                                    }`}
                                    onClick={() => updateTimePeriod(period)}
                                >
                                    <span className='text-[11px] font-medium whitespace-nowrap'>
                                        {timePeriodTranslations[period] || period}
                                    </span>
                                    {hasVal && (
                                        <span
                                            dir='ltr'
                                            className={`inline-flex items-center gap-0.5 text-[10px] leading-none font-sans whitespace-nowrap ${
                                                // ≥ 4.5:1 on every button state (light: white / grey track; dark: track / selected)
                                                isPos
                                                    ? `text-emerald-700 ${isActive ? 'dark:text-emerald-300' : 'dark:text-emerald-400'}`
                                                    : `text-rose-600 ${isActive ? 'dark:text-rose-300' : 'dark:text-rose-400'}`
                                            }`}
                                        >
                                            {isPos ? (
                                                <ArrowUp className='size-2.5 stroke-[2.5]' />
                                            ) : (
                                                <ArrowDown className='size-2.5 stroke-[2.5]' />
                                            )}
                                            {toPersianNumber(Math.abs(changeVal).toFixed(2))}%
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SingleCoinMainChart;
